import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import { BACKEND, MASCOT, api } from "@/lib/api";

const sessionId = () => {
  let s = localStorage.getItem("faunny_chat");
  if (!s) {
    s = `s-${crypto.randomUUID()}`;
    localStorage.setItem("faunny_chat", s);
  }
  return s;
};

const renderInline = (text, onNav) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const href = link[2];
      return href.startsWith("/") ? <Link key={i} to={href} onClick={onNav}>{link[1]}</Link> : <span key={i}>{link[1]}</span>;
    }
    const bold = p.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i}>{renderInline(bold[1], onNav)}</strong>;
    return <Fragment key={i}>{p}</Fragment>;
  });
};

export const Markdown = ({ text, onNav }) => (
  <div className="chat-md space-y-1.5">
    {text.split("\n").filter((l) => l.trim()).map((line, i) => {
      const li = line.match(/^\s*[-*•]\s+(.*)/);
      const h = line.match(/^#+\s+(.*)/);
      if (h) return <p key={i} className="font-bold">{renderInline(h[1], onNav)}</p>;
      return li ? <p key={i} className="pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-[#DC2626]">{renderInline(li[1], onNav)}</p>
        : <p key={i}>{renderInline(line, onNav)}</p>;
    })}
  </div>
);

const SUGGESTIONS = [
  "Mi caballo compite en salto y tiene 12 años, ¿qué me recomiendas?",
  "Mi yegua se rasca mucho en verano",
  "Vamos a viajar en van a un concurso y se pone nervioso",
];

export const AdvisorChat = ({ compact, onNav }) => {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => {
    api.get(`/advisor/history/${sid.current}`).then((r) => setMsgs((cur) => [...r.data.map((m) => ({ role: m.role, content: m.content })), ...cur])).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND}/api/advisor/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: msg }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const text = typeof d.detail === "string" ? d.detail : "No he podido responder. Inténtalo de nuevo.";
        setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: text } : x)));
        setBusy(false);
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: x.content + chunk } : x)));
      }
    } catch {
      setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: "No he podido responder. Inténtalo de nuevo." } : x)));
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col h-full" data-testid="advisor-chat">
      <div className={`flex-1 overflow-y-auto space-y-3 ${compact ? "p-4" : "p-5"}`} data-testid="advisor-messages">
        {msgs.length === 0 && (
          <div className="space-y-3">
            <div className="flex gap-3 items-start">
              <img src={MASCOT} alt="Dr. FAUNNY" className="w-10 h-10 rounded-full object-cover object-center shrink-0 border-2 border-[#DC2626]" />
              <div className="bg-[#EFF6FF] text-[#0B1D3A] text-sm rounded-lg rounded-tl-none p-3">
                ¡Hola! Soy el Asesor FAUNNY. Cuéntame cómo es tu caballo (edad, actividad, qué le pasa) y te recomiendo los productos más adecuados.
              </div>
            </div>
            {SUGGESTIONS.map((s, i) => (
              <button key={s} onClick={() => send(s)} data-testid={`advisor-suggestion-${i}`}
                className="block w-full text-left text-sm border border-slate-200 hover:border-[#2563EB] rounded-lg px-3 py-2 text-slate-700 transition-colors">
                {s}
              </button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`advisor-msg-${m.role}`}
              className={`max-w-[85%] text-sm rounded-lg p-3 ${m.role === "user" ? "bg-[#0B1D3A] text-white rounded-br-none" : "bg-[#EFF6FF] text-[#0F172A] rounded-tl-none"}`}>
              {m.role === "user" ? m.content : m.content ? <Markdown text={m.content} onNav={onNav} /> : <span className="animate-pulse">Pensando…</span>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-slate-200 p-3 flex gap-2 bg-white">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Describe a tu caballo…" data-testid="advisor-input"
          className="flex-1 text-sm border border-slate-200 rounded-full px-4 h-11 outline-none focus:border-[#2563EB]" />
        <button disabled={busy} data-testid="advisor-send-button" aria-label="Enviar"
          className="w-11 h-11 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-slate-300 text-white grid place-items-center transition-colors">
          <Send size={17} />
        </button>
      </form>
      <p className="text-[11px] text-slate-500 text-center pb-2 bg-white">Orientación general. No sustituye al veterinario.</p>
    </div>
  );
};
